import { getBranding } from "client/context/branding-context";
import { FieldSet } from "client/lib/apt-merchant";
import { getIntl } from "client/utils/internationalization";

import { FieldError, Merchant } from "..";

import {
  FieldDataType,
  I18nValues,
  MerchantField,
  UiFieldType,
  UiInputBaseProps,
} from "./types/base";

/*
 * T: The type of value that this field will hold
 * M: A concrete MerchantModel
 *   used whenever a property on field is dependent upon model
 *   e.g. Business Type depends on Registered Country
 */

export abstract class Field<T> {
  protected readonly _model: Merchant;
  protected readonly _fieldSet?: FieldSet<any>;
  protected _value: T | undefined;

  // these properties are all static
  // (their values do not depend on the model's state)
  protected readonly _dataType: FieldDataType;
  protected readonly _propertyName: string;
  protected readonly _uiFieldType?: UiFieldType;
  protected readonly _uiInputBaseProps?: UiInputBaseProps;
  protected readonly _testValue?: T;

  // Compliance Fields
  protected _salesforceFieldNames?: string;
  protected _salesforceObjects?: string;

  // these properties are all dynamic
  // (their values may be dependent on the model's state)
  protected readonly _pickList?: T[] | null;
  // _applicable determines whether or not the current field
  // is relevant (or ... applicable) given the current state of
  // the model. An example - Store Locations are not applicable
  // if a user selects "Online Only" for sales channel, since Store
  // Locations are only relevant to physical stores ("Instore")
  protected readonly _applicable?: boolean;
  protected readonly _required?: boolean;
  // _shown determines whether or not a field shows up in the UI.
  // Analogous to visible. A field MAY be applicable, but not shown.
  // An example might be email - where the form data being passed to
  // the backend requires the email field, but its not part of the
  // visible UI
  protected readonly _shown?: boolean;
  protected readonly _uploadFilename?: string;
  protected readonly _uploadFileExtensions?: string; // Ex: "image/png, image/jpeg";

  constructor(
    model: Merchant,
    {
      dataType,
      testValue,
      propertyName,
      uiFieldType,
      uiInputBaseProps,

      salesforceFieldNames,
      salesforceObjects,

      applicable,
      pickList = null,
      uploadFilename,
      uploadFileExtensions,
      required,
      shown,
      value,
    }: MerchantField<T>,
    fieldSet?: FieldSet<any>
  ) {
    this._model = model;
    this._value = value;
    this._fieldSet = fieldSet;

    this._dataType = dataType;
    this._testValue = testValue;
    this._propertyName = propertyName;
    this._uiFieldType = uiFieldType;
    this._uiInputBaseProps = uiInputBaseProps;

    this._salesforceFieldNames = salesforceFieldNames;
    this._salesforceObjects = salesforceObjects;

    this._applicable = applicable;
    this._pickList = pickList;
    this._uploadFilename = uploadFilename;
    this._uploadFileExtensions = uploadFileExtensions;
    this._required = required;
    this._shown = shown;
  }

  get dataType(): FieldDataType {
    return this._dataType;
  }

  get testValue(): T | undefined {
    return this._testValue;
  }

  get salesforceObjects(): string | undefined {
    return this._salesforceObjects;
  }

  set salesforceObjects(v: string | undefined) {
    if (typeof window !== "undefined") {
      throw new Error(`Cannot set salesforce data to ${v}`);
    }
    this._salesforceObjects = v;
  }

  get salesforceFieldNames(): string | undefined {
    return this._salesforceFieldNames;
  }

  set salesforceFieldNames(v: string | undefined) {
    if (typeof window !== "undefined") {
      throw new Error(`Cannot set salesforce data to ${v}`);
    }
    this._salesforceFieldNames = v;
  }

  get value(): T | undefined {
    return this._value;
  }

  set value(v: T | undefined) {
    // trim whitespace
    this._value = typeof v === "string" ? ((v.trim() as unknown) as T) : v;
  }

  get propertyName(): string {
    return this._propertyName;
  }

  get uploadFilename(): string | undefined {
    return this._uploadFilename;
  }

  get uploadFileExtensions(): string | undefined {
    return this._uploadFileExtensions;
  }

  get i18nValues(): I18nValues {
    const country = this._model.companyInformation.registeredCountry.value;
    const brandTitle = getBranding();
    const t = getIntl(country);
    return {
      fieldHeader: t(`${this.propertyName}_FieldHeader`, { brandTitle }),
      helperText: t(`${this.propertyName}_HelperText`, { brandTitle }),
      label: t(this.propertyName, { brandTitle }),
      placeholder: t(`${this.propertyName}_Placeholder`, { brandTitle }),
    };
  }

  get uiFieldType(): UiFieldType | undefined {
    return this._uiFieldType;
  }

  get uiInputBaseProps(): UiInputBaseProps | undefined {
    return this._uiInputBaseProps;
  }

  get serializableFields(): MerchantField<T> {
    return {
      dataType: this.dataType,
      propertyName: this.propertyName,
      i18nValues: this.i18nValues,

      uiFieldType: this.uiFieldType,

      pickList: this.pickList,
      applicable: this.applicable,
      required: this.required,
      shown: this.shown,

      value: this.value,
    };
  }

  // fields with custom logic should override this accessor
  get pickList(): T[] | null {
    if (this._pickList !== undefined) {
      return this._pickList;
    }
    throw new Error(
      `Model config error missing "picklist" ${this._propertyName}`
    );
  }

  // fields with custom logic should override this accessor
  get applicable(): boolean {
    if (this._applicable !== undefined) {
      return this._applicable;
    }
    throw new Error(
      `Model config error missing "applicable" ${this._propertyName}`
    );
  }

  // fields with custom logic should override this accessor
  get required(): boolean {
    if (this._required !== undefined) {
      return this._required;
    }
    throw new Error(
      `Model config error missing "required" ${this._propertyName}`
    );
  }

  // fields with custom logic should override this accessor
  get shown(): boolean {
    if (this._shown !== undefined) {
      return this._shown;
    }
    throw new Error(`Model config error missing "shown" ${this._propertyName}`);
  }

  formatOnChange(value: T): T {
    if (this._formatOnChange) {
      return this._formatOnChange(value);
    }
    return value;
  }

  validate(): void {
    const hasValue = !!this.value; // //this.value //!== undefined

    // generic validation logic that can be shared across all fields
    if (this.required && !hasValue) {
      throw new FieldError(this, "Missing required field", "Required");
    }

    const fieldValue =
      typeof this.value === "string" ? this.value.trim() : this.value;

    if (
      hasValue &&
      this.required &&
      this.pickList &&
      this.uiFieldType === "Select"
    ) {
      //  some picklist have {value, label} types
      if (typeof this.pickList[0] !== "string" && "value" in this.pickList[0]) {
        const found = this.pickList.find(
          ({ value }: any) => value === fieldValue
        );
        if (!found) {
          throw new TypeError(
            `Field "${this.propertyName}" has invalid value "${fieldValue}"`
          );
        }
      } else if (this.pickList.indexOf(fieldValue as T) === -1) {
        throw new TypeError(
          `Field "${this.propertyName}" has invalid value "${fieldValue}"`
        );
      }
    }

    if (this.applicable && this._validate) {
      this._validate();
    }

    if (
      hasValue &&
      this.dataType === "number" &&
      typeof fieldValue === "string" &&
      !/^\d+$/i.test(fieldValue)
    ) {
      throw new FieldError(this, "Value must be a number", "InvalidTypeNumber");
    }
  }

  // fields with custom validation logic should override this method
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _validate(): void {}

  // fields with custom formatting when a value changes should override this method
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _formatOnChange(initialVal: T): T {
    return initialVal;
  }
}
